import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, TrendingUp } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import BriefingRequestModal from '../../components/BriefingRequestModal';
import { strategy } from '../../data/strategy';
import { MotionReveal } from '../../components/strategy/Motion';
import { CapitalBars, RoadmapArea } from '../../components/strategy/Charts';

const Capital = () => {
  const [audience, setAudience] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="App font-sans bg-white">
      <Header onPartnerClick={() => setAudience('capital')} />

      <section className="pt-32 pb-16 px-6 bg-[#0a1729] text-white">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8" data-testid="capital-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#7fd6c9] mb-3">Capital Architecture</div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold leading-tight max-w-4xl">
            Permanent capital. Disciplined leverage. Recycled cash flow.
          </h1>
          <p className="text-lg text-white/70 max-w-3xl mt-6">
            The $500B target is achieved by mobilising — not just raising — capital. BIG combines permanent equity, institutional debt, sovereign co-investment, trade finance and recycled cash flows.
          </p>
        </div>
      </section>

      {/* Capital progression */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#2f9e8e] mb-3">Permanent-Capital Programme</div>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1a365d] mb-10 max-w-3xl">
            A staged capital progression from sponsor commitment to public-market scale.
          </h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3">
            {strategy.capitalProgression.map((stage, i) => (
              <div key={stage.stage} className="bg-[#f6f9fb] border border-gray-100 rounded-xl p-5 relative">
                <div className="absolute -top-3 left-5 w-7 h-7 rounded-full bg-[#1a365d] text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                <div className="text-xs text-gray-500 mb-2 mt-2">Stage {i + 1}</div>
                <div className="font-serif text-lg font-bold text-[#1a365d] leading-tight mb-2">{stage.size}</div>
                <div className="text-xs text-gray-600 leading-relaxed">{stage.stage}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sources & Uses — bar charts */}
      <section className="py-16 px-6 bg-[#f6f9fb]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10">
            <MotionReveal>
              <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#2f9e8e] mb-3">Sources of the $500B</div>
              <h2 className="font-serif text-3xl font-bold text-[#1a365d] mb-6">Where capital comes from</h2>
              <div className="bg-white border border-gray-100 rounded-xl p-5">
                <CapitalBars
                  data={strategy.capitalSources.map((c) => ({ name: c.source, value: parseInt(String(c.amount).replace(/[^0-9]/g, ''), 10) }))}
                  color="#2f9e8e"
                  theme="light"
                  height={360}
                />
                <div className="flex items-center justify-between pt-4 mt-2 border-t border-gray-100">
                  <span className="text-sm font-semibold text-[#1a365d]">Total cumulative</span>
                  <span className="text-xl font-bold text-[#2f9e8e]">$500B</span>
                </div>
              </div>
            </MotionReveal>
            <MotionReveal delay={120}>
              <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#c19a3c] mb-3">Uses of the $500B</div>
              <h2 className="font-serif text-3xl font-bold text-[#1a365d] mb-6">Where capital goes</h2>
              <div className="bg-white border border-gray-100 rounded-xl p-5">
                <CapitalBars
                  data={strategy.capitalUses.map((c) => ({ name: c.use, value: parseInt(String(c.amount).replace(/[^0-9]/g, ''), 10) }))}
                  color="#c19a3c"
                  theme="light"
                  height={360}
                />
                <div className="flex items-center justify-between pt-4 mt-2 border-t border-gray-100">
                  <span className="text-sm font-semibold text-[#1a365d]">Total deployed</span>
                  <span className="text-xl font-bold text-[#c19a3c]">$500B</span>
                </div>
              </div>
            </MotionReveal>
          </div>

          {/* Cumulative deployment curve */}
          <MotionReveal className="mt-10">
            <div className="bg-white border border-gray-100 rounded-xl p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#2f9e8e] font-bold">Cumulative deployment over the 20-year arc</div>
                <div className="text-xs text-gray-500">$0 → $500B</div>
              </div>
              <RoadmapArea
                data={[
                  { phase: 'Phase I',  value: 5 },
                  { phase: 'Phase II', value: 30 },
                  { phase: 'Phase III',value: 125 },
                  { phase: 'Phase IV', value: 275 },
                  { phase: 'Phase V',  value: 500 },
                ]}
                theme="light"
                height={260}
              />
            </div>
          </MotionReveal>
        </div>
      </section>

      {/* What counts / doesn't count */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#2f9e8e] mb-3">Discipline</div>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1a365d] mb-10 max-w-3xl">
            The $500B is real deployment — not announcements.
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-[#f0faf7] border border-[#2f9e8e]/30 rounded-xl p-7">
              <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-4">What counts</h3>
              <ul className="space-y-2 text-sm text-gray-800">
                {['BIG equity & sponsored vehicles', 'Project, acquisition & corporate debt', 'Trade & commodity finance', 'Export-credit financing', 'Sovereign, DFI & MDB co-investment', 'Strategic corporate & offtake capital', 'Reinvested operating cash flow', 'Refinancing used to expand productive assets'].map((x) => (
                  <li key={x} className="flex items-start gap-2"><TrendingUp className="w-4 h-4 text-[#2f9e8e] flex-shrink-0 mt-0.5" /> {x}</li>
                ))}
              </ul>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-7">
              <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-4">What will NOT count</h3>
              <ul className="space-y-2 text-sm text-gray-800">
                {['Non-binding memoranda of understanding', 'Uncommitted project pipelines', 'Advisory mandates without capital deployment', 'Assets where BIG has no material role', 'Repeated refinancing without new investment', 'Government announcements without signed financing', 'Gross trading volumes unrelated to committed finance'].map((x) => (
                  <li key={x} className="flex items-start gap-2"><span className="text-red-500 mt-0.5">✕</span> {x}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-[#0a1729] text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">Anchor or co-invest in the founding round.</h2>
          <button onClick={() => setAudience('capital')} className="bg-[#2f9e8e] hover:bg-[#268578] px-7 py-4 rounded-lg font-semibold inline-flex items-center gap-2" data-testid="capital-cta">
            Capital Partner Briefing <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <Footer />
      {audience && <BriefingRequestModal isOpen={!!audience} onClose={() => setAudience(null)} audience={audience} />}
    </div>
  );
};

export default Capital;
